import React, { useState } from 'react'
import { useMutation } from '@apollo/client'

import { renderArtTag } from '../Labels/ArtTag'
import { printPNGFromURL } from '../Daemon/Print'
import type { CoverFragment } from '../graphql/__generated__'
import { UpdateCustomCoverApprovalDocument, UpdateCoverPrintCountDocument } from '../graphql/__generated__'
import { filenameWithoutExt } from '../FinalizeDialog/LabelSection'

type DestinationFragment = NonNullable<CoverFragment['usedByDestinations']>[number]

interface Props {
  art: CoverFragment[]
}

type PrintQuantity = {
  needed: number
  printed: number
  remaining: number
}

const PRINTERS = [
  'lower',
  'upper',
]

type CurrentlyPrinting = {
  [key: string]: string | undefined,
}

export function getPrintQuantity(cover: CoverFragment): PrintQuantity | null {
  if (!cover?.usedByDestinations) {
    return null
  }

  let neededQty = 0
  let printedQty = 0
  for (let i=0; i < cover.usedByDestinations.length; i++){
    const dest = cover.usedByDestinations[i]
    if (dest.state === 'paid' || dest.state === 'hold'){
      neededQty += dest.quantity
      printedQty += dest.coversPrinted
    }
  }

  return {
    needed: neededQty,
    printed: printedQty,
    remaining: neededQty - printedQty,
  }
}

export default function ArtList({ art }: Props){
  const [updateCustomCoverApproval, { loading: loadingApproval, error: approvalError }] = useMutation(UpdateCustomCoverApprovalDocument)
  const [updateCoverPrintCount, { loading: loadingCoverPrintCount, error: coverPrintCountError }] = useMutation(UpdateCoverPrintCountDocument)
  const [currentlyPrinting, setCurrentlyPrinting] = useState<CurrentlyPrinting>({ lower: undefined, upper: undefined })

  const currentlyPrintingOn = (coverId: string): string | undefined => {
    for (let i=0; i < PRINTERS.length; i++){
      const printer = PRINTERS[i]
      if (currentlyPrinting[printer] === coverId){
        return printer
      }
    }
    return undefined
  }

  // sort art by the highest order name in the destinations,
  // such that our prints will be done in rough name order.
  const sortedArt = art.sort((a, b) => {
    const aDests = a.usedByDestinations
    const bDests = b.usedByDestinations
    if (!aDests || !bDests) return 0
    const aDest = [...aDests].sort((x, y) => +(y.order?.name ?? '0') - +(x.order?.name ?? '0'))[0]
    const bDest = [...bDests].sort((x, y) => +(y.order?.name ?? '0') - +(x.order?.name ?? '0'))[0]
    if (!aDest || !bDest || !aDest.order || !bDest.order) return 0
    return +aDest.order.name > +bDest.order.name ? 1 : -1
  })

  const setApproval = (coverId: string, approved: boolean) => {
    updateCustomCoverApproval({
      variables: {
        coverID: coverId,
        approved,
      }
    })
  }

  const increment = (coverId: string, incr: number) => {
    let incrementDest: DestinationFragment | undefined = undefined

    for (let k=0; k < art.length; k++){
      if (art[k].id === coverId){
        const dests = art[k].usedByDestinations ?? []
        for (let i=0; i < dests.length; i++) {
          const dest = dests[i]

          // If all the dests are full, we just pick the last one
          incrementDest = dest

          if (dest.coversPrinted < dest.quantity) {
            break
          }
        }

        if (!incrementDest) {
          throw new Error("No destinations found to increment cover count")
        }

        updateCoverPrintCount({
          variables: {
            destinationID: incrementDest.id,
            oldValue: incrementDest.coversPrinted,
            newValue: incrementDest.coversPrinted + incr,
          },
        })

        break
      }
    }
  }

  const markPrinting = (printer: string, coverId: string) => {
    console.log('marking printing', printer, coverId)
    currentlyPrinting[printer] = coverId
    setCurrentlyPrinting({ ...currentlyPrinting })
  }

  const printDone = (printer: string, coverId: string) => {
    for (let i=0; i < art.length; i++){
      if (art[i].id === coverId){
        const dests = art[i].usedByDestinations ?? []
        let totalQty = 0
        let printedQty = 1
        for (let k=0; k < dests.length; k++){
          totalQty += dests[k].quantity
          printedQty += dests[k].coversPrinted
        }
        if (printedQty >= totalQty){
          currentlyPrinting[printer] = undefined
          setCurrentlyPrinting({ ...currentlyPrinting })
        }

        break
      }
    }

    increment(coverId, 1)
  }

  const printCancelled = (printer: string, coverId: string) => {
    currentlyPrinting[printer] = undefined
    setCurrentlyPrinting({ ...currentlyPrinting })
  }

  const printTag = (orderName: string, coverName: string, qty: number) => {
    renderArtTag(orderName, coverName).then((canvasRef) => {
      if (!canvasRef || !canvasRef.canvas){
        return
      }

      const image = canvasRef.canvas.toDataURL('image/png')
      printPNGFromURL("2x1", image)
    })
  }

  const printAllTags = (coverId: string) => {
    // We need to come up with labels for all the prints. A single
    // order can include multiple designs, and we don't want swap
    // them with other covers for the same order. To avoid that,
    // we need to label each cover with the design it's meant for.
    // In the UI we label the designs with OrderName:DesignIndex, after
    // sorting the designs by id, so we will do the same here.
    type DesignEntry = { designId: string; entries: { orderName: string; coverName: string; quantity: number }[] }
    let allByOrder: { [orderId: string]: DesignEntry[] } = {}
    for (let k=0; k < art.length; k++){
      if (art[k].id !== coverId){
        continue
      }

      const dests = art[k].usedByDestinations ?? []
      for (let i=0; i < dests.length; i++) {
        const dest = dests[i]
        if (!allByOrder[dest.orderId]){
          allByOrder[dest.orderId] = []
        }

        let entryIndex = Object.values(allByOrder[dest.orderId])
          .findIndex((entry: DesignEntry) => entry.designId === dest.designId)

        if (entryIndex === -1) {
          entryIndex = allByOrder[dest.orderId].length
          allByOrder[dest.orderId].push({
            designId: dest.designId,
            entries: [],
          })
        }

        allByOrder[dest.orderId][entryIndex].entries.push({
          orderName: dest.order?.name ?? '',
          coverName: filenameWithoutExt(art[k].productionImageURL ?? ''),
          quantity: dest.quantity,
        })
      }
    }

    for (let orderId in allByOrder){
      const order = allByOrder[orderId]
      // sort by designId, as that's what we use when indexing designs
      // in the main listing.

      order.sort((a, b) => (a.designId > b.designId) ? 1 : -1)

      for (let k=0; k < order.length; k++){
        const design = order[k]

        for (let i=0; i < design.entries.length; i++){
          const entry = design.entries[i]
          for (let j=0; j < entry.quantity; j++){
            printTag(entry.orderName + ":" + k + ":" + i, entry.coverName, 1)
          }
        }
      }
    }
  }

  return <div className="pl-8">
    { approvalError && <p className="bg-red-100 p-4 mb-2">Error approving custom cover.</p> }
    { coverPrintCountError && <p className="bg-red-100 p-4 mb-2">Error saving cover print count.</p> }

    <ul>
      <li className="grid grid-cols-12 font-bold mb-4 pb-1 border-b">
        <div />
        <div>Approval { loadingApproval ? "S" : ""  }</div>
        <div>Order</div>
        <div>Name</div>
        <div className="col-span-2">Print</div>
        <div>Needed</div>
        <div className="col-span-2">Printed { loadingCoverPrintCount ? "S" : "" }</div>
        <div>Remaining</div>
        <div>Tags</div>
      </li>

      { art.map((cover) => {
        const qty = getPrintQuantity(cover)
        if (qty === null) {
          return null
        }
        const { needed, printed, remaining } = qty

        const productionImageURL = cover.productionImageURL ?? ''
        const name = filenameWithoutExt(productionImageURL)
        const coverDests = cover.usedByDestinations ?? []

        const done = remaining <= 0
        return <li key={ cover.id } className={ "grid grid-cols-12 mb-2 " + (done ? 'bg-green-100' : '') }>
          <div className="col-span-1 py-2 group relative">
            <img className="w-8 inline-block mr-4" src={ cover.thumbnailImageURL ?? undefined } />
            <a
              href={ `https://make.sendheirloom.com/sudo/ownerOfOrderID:${ coverDests[0]?.orderId }?next=cover/${ cover.id }` }
              target="_blank"
              className="underline">edit</a>
            <div className="absolute left-8 hidden group-hover:block z-10">
              <div className="w-96 bg-black p-4">
                <img className="w-full" src={ productionImageURL } />
              </div>
            </div>
          </div>

          <div className="col-span-1">
            { 
              cover.approvedAt
              ? <div>
                  <button
                    className="p-2 ml-2 bg-red-300 text-white"
                    onClick={ () => setApproval(cover.id, false) }>Cancel</button>
                </div>
              : <div>
                  <button
                    className="p-2 ml-2 bg-gray-200"
                    onClick={ () => setApproval(cover.id, true) }>Approve</button>
                </div>
            }
          </div>

          <div className="col-span-1 py-2">
            { coverDests.map((dest) => dest.order?.name).join(", ") }
          </div>

          <div className="col-span-1 py-2">
            <a href={ productionImageURL } target="_blank" className="underline">
              { name.substring(0, 3) }
            </a>
          </div>

          <div className="col-span-2 py-2 space-y-2">
            { Object.values(PRINTERS).map((printer) => {
              if (currentlyPrintingOn(cover.id) === printer) {
                return <div className="inline-block bg-gray-100 p-1 mr-2 space-y-1" key={ printer }>
                  <button
                    onClick={ () => printDone(printer, cover.id) }
                    className="bg-green-200 p-1 mr-2">Done</button>
                  <button
                    onClick={ () => printCancelled(printer, cover.id) }
                    className="bg-red-200 p-1">Cancel</button>
                </div>
              } else {
                return <a
                  key={ printer }
                  className="bg-gray-200 p-2 ml-2"
                  href={ productionImageURL }
                  target="_blank"
                  onClick={ () => markPrinting(printer, cover.id) }>{ printer.substring(0, 1).toUpperCase() }</a>
              }
            }) }
          </div>

          <div className="py-2">
            { needed }
          </div>

          <div className="col-span-2">
            { printed }

            <button
              className="bg-gray-200 p-2 ml-2"
              onClick={ () => increment(cover.id, -1) }>-</button>
            <button
              className="bg-gray-200 p-2 ml-2"
              onClick={ () => increment(cover.id, 1) }>+</button>
          </div>

          <div className="py-2">
            { remaining }
          </div>

          <div>
            { coverDests.length <= 1 &&
              <button
                className="bg-gray-200 p-2 ml-2"
                onClick={ () => printTag(coverDests[0]?.order?.name ?? '', name, 1) }>1</button>
            }
            <button
              className="bg-gray-200 p-2 ml-2"
              onClick={ () => printAllTags(cover.id) }>All</button>
          </div>
        </li>
      }) }
    </ul>
  </div>
}
